import axios from 'axios';
import { clearAuthSession, getAuthToken } from './auth-storage';

const backendOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
// Local CRA cannot call the production API directly (browser CORS). Dev server proxies /api.
const isLocalDevHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_URL = isLocalDevHost ? '/api' : `${backendOrigin}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Instance default is application/json — must not apply to FormData
  // (boundary missing → FastAPI "file" field 422 / upload fails).
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthSession();
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  sessionData: (sessionId) => api.get('/auth/session-data', { headers: { 'X-Session-ID': sessionId } }),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// Products
export const products = {
  list: (params, config = {}) => api.get('/products', { params, ...config }),
  aiSearch: (data, config = {}) => api.post('/search/ai', data, config),
  aiEvent: (data) => api.post('/search/ai/event', data),
  /** @deprecated Navbar vb. için — her zaman `list` kullanın */
  getAll: (params, config = {}) => api.get('/products', { params, ...config }),
  get: (id, config = {}) => api.get(`/products/${id}`, config),
  getSchemaJsonLd: (idOrSlug, lang = 'tr', config = {}) =>
    api.get(`/products/${encodeURIComponent(idOrSlug)}/schema-jsonld`, { params: { lang }, ...config }),
  create: (data) => api.post('/products', data),
  analyzeImages: (formData) => api.post('/ai/products/analyze', formData, { timeout: 130000 }),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Categories
export const categories = {
  list: (config = {}) => api.get('/categories', config),
  get: (id) => api.get(`/categories/${id}`),
};

// Cart
export const cart = {
  get: () => api.get('/cart'),
  add: (data) => api.post('/cart/add', data),
  update: (data) => api.put('/cart/update', data),
  clear: () => api.delete('/cart/clear'),
};

// Orders
export const orders = {
  list: () => api.get('/orders'),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  createGuest: (data) => api.post('/orders/guest', data),
  updateStatus: (id, status) => api.put(`/orders/${id}/status?status=${status}`),
  /** Ödeme başarısız — sepeti siparişten geri yükle */
  checkoutRetry: (orderId, email) =>
    api.get(`/orders/${orderId}/checkout-retry`, { params: email ? { email } : {} }),
};

// Reviews
export const reviews = {
  get: (productId) => api.get(`/reviews/${productId}`),
  create: (data) => api.post('/reviews', data),
};

/** Genel iş / kariyer başvurusu (misafir) */
export const careers = {
  apply: (data) => api.post('/careers/apply', data),
};

// Chat (ana site widget → /api/chat, admin listesi chat_messages)
export const chat = {
  send: (data) => api.post('/chat', data, { timeout: 60000 }),
  history: (sessionId) => api.get(`/chat/history/${sessionId}`),
  /** Akıllı yanıtlar / hızlı butonları DB'ye yazar (admin panelde görünür) */
  persistExchange: (data) => api.post('/chat/persist-exchange', data, { timeout: 20000 }),
};

// Seller Product AI Chat
export const sellerProductChat = {
  send: (data) => api.post('/seller/product-chat', data),
  upload: (formData, onUploadProgress) => api.post('/seller/product-chat/upload', formData, {
    onUploadProgress
  }),
  requestPromotion: (productIds, sessionId, language) => api.post('/seller/product-chat/request-promotion', null, {
    params: { product_ids: productIds, session_id: sessionId, language }
  }),
  removeProducts: (productIds, language) => api.delete('/seller/product-chat/products', {
    data: { product_ids: productIds },
    params: { language }
  }),
};

// Payments
export const payments = {
  checkout: (orderId, originUrl) => api.post(`/payments/checkout?order_id=${orderId}`, { origin_url: originUrl }),
  guestCheckout: (orderId, guestEmail, originUrl) => api.post(`/payments/guest-checkout?order_id=${orderId}&guest_email=${encodeURIComponent(guestEmail)}`, { origin_url: originUrl }),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getPublicKey: () => api.get('/payments/public-key'),
};

// Revolut Payment API
export const revolut = {
  createOrder: (data) => api.post('/revolut/create-order', data),
  getOrder: (orderId) => api.get(`/revolut/order/${orderId}`),
  refund: (data) => api.post('/revolut/refund', data),
  getSupportedCurrencies: () => api.get('/revolut/supported-currencies'),
  getPublicKey: () => api.get('/revolut/public-key'),
};

// GTS Travel Flight API
export const flights = {
  search: (data) => api.post('/flights/search', data),
  getPrice: (data) => api.post('/flights/price', data),
  prepare: (data) => api.post('/flights/prepare', data),
  getPaymentConfig: () => api.get('/flights/payment-config'),
  getCancellationPolicy: (airline) =>
    api.get('/flights/cancellation-policy', airline ? { params: { airline } } : {}),
  book: (data) => api.post('/flights/book', data),
  getBooking: (bookingId) => api.get(`/flights/booking/${bookingId}`),
  cancelBooking: (bookingId) => api.post(`/flights/booking/${bookingId}/cancel`),
  listBookings: (params) => api.get('/flights/bookings', { params }),
  // Boş q göndermeyin: bazı sunucularda ?q= veya tek harf 422 üretebiliyor
  // fromCode: kalkış seçiliyse o rotadaki varışları önce sıralar (sert filtre değil)
  getAirports: (query = '', { fromCode, toCode, limit = 600 } = {}) => {
    const q = String(query ?? '').trim();
    const params = { limit };
    if (q) params.q = q;
    if (fromCode) params.from_code = String(fromCode).trim().toUpperCase().slice(0, 3);
    if (toCode) params.to_code = String(toCode).trim().toUpperCase().slice(0, 3);
    return api.get('/flights/airports', { params });
  },
  getHandlingFee: () => api.get('/flights/handling-fee'),
  health: () => api.get('/flights/health'),
};

// Seller
// Public seller storefronts
export const stores = {
  get: (identifier) => api.get(`/stores/${identifier}`),
  products: (identifier, params) => api.get(`/stores/${identifier}/products`, { params }),
};

export const seller = {
  getStoreProfile: () => api.get('/seller/store-profile'),
  updateStoreProfile: (data) => api.put('/seller/store-profile', data),
  products: (params) => api.get('/seller/products', { params }),
  exportProductsXml: () => api.get('/seller/products/export-xml', { responseType: 'blob' }),
  orders: () => api.get('/seller/orders'),
  getOrder: (orderId) => api.get(`/seller/orders/${orderId}`),
  updateOrder: (orderId, data) => api.put(`/seller/orders/${orderId}`, data),
  analytics: () => api.get('/seller/analytics'),
  bulkUpload: (formData) => api.post('/seller/products/bulk-upload', formData),
  getMarketPrice: (productName) => api.get(`/seller/products/market-price?product_name=${encodeURIComponent(productName)}`),
  translateProduct: (productId, languages) => api.post(`/seller/products/${productId}/translate`, { target_languages: languages }),
  getTranslatedProduct: (productId, lang) => api.get(`/seller/products/${productId}/translated?lang=${lang}`),
  createCategory: (data) => api.post('/seller/categories', data),
  // Car Rentals
  carRentals: () => api.get('/seller/car-rentals'),
  carBookings: () => api.get('/seller/car-bookings'),
  updateCarBookingStatus: (bookingId, status) => api.put(`/seller/car-bookings/${bookingId}/status?status=${status}`),
  // Hotels & Flights
  hotels: () => api.get('/seller/hotels'),
  flights: () => api.get('/seller/flights'),
  hotelReservations: () => api.get('/seller/hotel-reservations'),
  flightReservations: () => api.get('/seller/flight-reservations'),
  updateHotelReservationStatus: (reservationId, status) => api.put(`/seller/hotel-reservations/${reservationId}/status?status=${status}`),
  updateFlightReservationStatus: (reservationId, status) => api.put(`/seller/flight-reservations/${reservationId}/status?status=${status}`),
  // Restaurants
  restaurants: () => api.get('/seller/restaurants'),
  createRestaurant: (data) => api.post('/seller/restaurants', data),
  updateRestaurant: (id, data) => api.put(`/seller/restaurants/${id}`, data),
  deleteRestaurant: (id) => api.delete(`/seller/restaurants/${id}`),
  addMenuItem: (restaurantId, data) => api.post(`/seller/restaurants/${restaurantId}/menu`, data),
  updateMenuItem: (restaurantId, itemId, data) => api.put(`/seller/restaurants/${restaurantId}/menu/${itemId}`, data),
  deleteMenuItem: (restaurantId, itemId) => api.delete(`/seller/restaurants/${restaurantId}/menu/${itemId}`),
  restaurantOrders: () => api.get('/seller/restaurant-orders'),
  updateRestaurantOrderStatus: (orderId, status) => api.put(`/seller/restaurant-orders/${orderId}/status?status=${status}`),
};

// Car Rentals (seller inventory — legacy)
export const carRentals = {
  list: (params) => api.get('/car-rentals', { params }),
  get: (id) => api.get(`/car-rentals/${id}`),
  create: (data) => api.post('/car-rentals', data),
  update: (id, data) => api.put(`/car-rentals/${id}`, data),
  delete: (id) => api.delete(`/car-rentals/${id}`),
  getAvailability: (id, startDate, endDate) => api.get(`/car-rentals/${id}/availability`, { params: { start_date: startDate, end_date: endDate } }),
  updateAvailability: (id, availability) => api.put(`/car-rentals/${id}/availability`, availability),
  createBooking: (data) => api.post('/car-rentals/bookings', data),
  myBookings: () => api.get('/car-rentals/bookings/my'),
  checkChassis: (chassisNumber) => api.get(`/car-rentals/check-chassis/${chassisNumber}`),
};

// KolayCAR Broker (public car rental)
export const carBroker = {
  status: () => api.get('/car-broker/status'),
  locations: (params = {}) => {
    const p =
      typeof params === 'string'
        ? { languageCode: params }
        : { languageCode: 'TR', limit: 40, ...params };
    return api.get('/car-broker/locations', { params: p });
  },
  cities: (languageCode = 'TR') =>
    api.get('/car-broker/locations/cities', { params: { languageCode } }),
  countries: (languageCode = 'TR') =>
    api.get('/car-broker/locations/countries', { params: { languageCode } }),
  searchVehicles: (params) => api.get('/car-broker/vehicles', { params }),
  vehiclesList: (params) => api.get('/car-broker/vehicles/list', { params }),
  mobileVehicles: (params, body = {}) =>
    api.post('/car-broker/vehicles/mobile', body, { params }),
  extras: (reservationToken, languageCode = 'TR') =>
    api.get('/car-broker/extras', { params: { reservationToken, languageCode } }),
  extrasList: (params) => api.get('/car-broker/extras/list', { params }),
  getReservation: (params) => api.get('/car-broker/reservations', { params }),
  postReservation: (data) => api.post('/car-broker/reservations', data),
  postReservationV2: (data) => api.post('/car-broker/reservations/v2', data),
  saveReservation: (data) => api.post('/car-broker/reservations/save', data),
  cancelReservation: (params) =>
    api.post('/car-broker/reservations/cancel', null, { params }),
  createBooking: (data) => api.post('/car-broker/bookings/create', data),
  confirmPayment: (bookingId, transactionId) =>
    api.post(`/car-broker/bookings/${bookingId}/confirm-payment`, null, {
      params: transactionId ? { transaction_id: transactionId } : undefined,
    }),
  cancelBooking: (bookingId, cancelNote) =>
    api.post(`/car-broker/bookings/${bookingId}/cancel`, null, {
      params: cancelNote ? { cancelNote } : undefined,
    }),
  myBookings: () => api.get('/car-broker/bookings/my'),
  getBooking: (bookingId) => api.get(`/car-broker/bookings/${bookingId}`),
};

// Hotels & Flights
export const hotelsFlights = {
  // Hotels
  listHotels: (params) => api.get('/hotel-flights/hotels', { params }),
  getHotel: (id) => api.get(`/hotel-flights/hotels/${id}`),
  createHotel: (data) => api.post('/hotel-flights/hotels', data),
  updateHotel: (id, data) => api.put(`/hotel-flights/hotels/${id}`, data),
  deleteHotel: (id) => api.delete(`/hotel-flights/hotels/${id}`),
  // Flights
  listFlights: (params) => api.get('/hotel-flights/flights', { params }),
  getFlight: (id) => api.get(`/hotel-flights/flights/${id}`),
  createFlight: (data) => api.post('/hotel-flights/flights', data),
  updateFlight: (id, data) => api.put(`/hotel-flights/flights/${id}`, data),
  deleteFlight: (id) => api.delete(`/hotel-flights/flights/${id}`),
  // Reservations
  createHotelReservation: (hotelId, data) => api.post(`/hotel-flights/hotels/${hotelId}/reserve`, data),
  createFlightReservation: (flightId, data) => api.post(`/hotel-flights/flights/${flightId}/reserve`, data),
  myHotelReservations: () => api.get('/hotel-flights/reservations/my-hotels'),
  myFlightReservations: () => api.get('/hotel-flights/reservations/my-flights'),
  // Mock Integrations
  searchBookingCom: (params) => api.get('/hotel-flights/integrations/booking-search', { params }),
  searchAmadeus: (params) => api.get('/hotel-flights/integrations/amadeus-flights', { params }),
};

// Tursys Hotel API V2
export const tursysHotels = {
  // Health check
  health: () => api.get('/hotels/tursys/health'),
  // Autocomplete - search destinations and hotels
  autocomplete: (query, language = 'TR', onlyHotels = false) => 
    api.post('/hotels/tursys/autocomplete', { query, language, only_hotels: onlyHotels }),
  // Search hotels
  search: (params) => api.post('/hotels/tursys/search', params),
  // Get hotel details and room offers
  detail: (params) => api.post('/hotels/tursys/detail', params),
  // Filter search results
  filter: (poolSearchId, filters) => api.post('/hotels/tursys/filter', { pool_search_id: poolSearchId, ...filters }),
  // Get static autocomplete data
  staticAutocomplete: () => api.get('/hotels/tursys/static-autocomplete'),
};

// Hotel Booking
export const hotelBooking = {
  create: (data) => api.post('/hotel-booking/create', data),
  get: (bookingId) => api.get(`/hotel-booking/${bookingId}`),
  confirmPayment: (bookingId, transactionId) => api.post(`/hotel-booking/${bookingId}/confirm-payment`, { transaction_id: transactionId }),
  cancel: (bookingId) => api.post(`/hotel-booking/${bookingId}/cancel`),
  getUserBookings: (email) => api.get(`/hotel-booking/user/${email}`),
};

// Admin
export const admin = {
  users: () => api.get('/admin/users'),
  stats: () => api.get('/admin/stats'),
  aiSearchStats: () => api.get('/search/ai/stats'),
  matchProducts: (data) => api.post('/search/match', data),
  dashboardChart: (days = 30) => api.get('/admin/dashboard-chart', { params: { days } }),
  getActivityLogs: (params) => api.get('/admin/activity-logs', { params }),
  updateRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  products: (params) => api.get('/admin/products', { params }),
  getProduct: (productId) => api.get(`/admin/products/${productId}`),
  updateProduct: (productId, data) => api.put(`/admin/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/admin/products/${productId}`),
  orders: () => api.get('/admin/orders'),
  /** Otel rezervasyonları (hotel_bookings koleksiyonu — marketplace siparişi değil) */
  hotelBookings: (params = {}) => api.get('/admin/hotel-bookings', { params }),
  /** Uçuş rezervasyonları (flight_bookings koleksiyonu) */
  flightBookings: (params = {}) => api.get('/admin/flight-bookings', { params }),
  /** Araç kiralama (KolayCAR broker — car_broker_bookings) */
  carBrokerBookings: (params = {}) => api.get('/admin/car-broker-bookings', { params }),
  /** Limuzin / taksi talep formu (taxi_booking_requests) */
  taxiBookingRequests: (params = {}) => api.get('/admin/taxi-booking-requests', { params }),
  getOrderDetail: (orderId) => api.get(`/admin/orders/${orderId}`),
  updateOrder: (orderId, data) => api.put(`/admin/orders/${orderId}`, data),
  updateOrderStatus: (orderId, status) => api.put(`/admin/orders/${orderId}`, { status }),
  cancelOrder: (orderId, data = {}) => api.post(`/admin/orders/${orderId}/cancel`, data),
  deleteOrder: (orderId) => api.delete(`/admin/orders/${orderId}`),
  categories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (categoryId, data) => api.put(`/admin/categories/${categoryId}`, data),
  deleteCategory: (categoryId) => api.delete(`/admin/categories/${categoryId}`),
  
  // Seller Verification
  pendingSellers: () => api.get('/admin/pending-sellers'),
  /** Menü rozeti için yalnızca bekleyen satıcı sayısı */
  pendingSellersCountOnly: () =>
    api.get('/admin/pending-sellers', { params: { count_only: true } }),
  allSellers: (status) => api.get('/admin/sellers', { params: status ? { status } : {} }),
  getSeller: (sellerId) => api.get(`/admin/sellers/${sellerId}`),
  getSellerDocuments: (sellerId) => api.get(`/admin/sellers/${sellerId}/documents`),
  approveSeller: (sellerId) => api.put(`/admin/sellers/${sellerId}/approve`),
  rejectSeller: (sellerId, reason) => api.put(`/admin/sellers/${sellerId}/reject`, null, { params: { reason } }),
  
  // User Notes
  getUserNotes: (userId) => api.get(`/admin/users/${userId}/notes`),
  addUserNote: (userId, content, noteType) => api.post(`/admin/users/${userId}/notes`, { content, note_type: noteType }),
  deleteUserNote: (userId, noteId) => api.delete(`/admin/users/${userId}/notes/${noteId}`),
  
  // User Management (Full Access)
  getUserDetail: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  resetUserPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  impersonateUser: (userId) => api.post(`/admin/users/${userId}/impersonate`),
  getUserProducts: (userId) => api.get(`/admin/users/${userId}/products`),
  
  // Product Management (Admin)
  updateProduct: (productId, data) => api.put(`/admin/products/${productId}`, data),
  
  // Role Management
  getRoles: () => api.get('/admin/roles'),
  getMyPermissions: () => api.get('/admin/roles/my-permissions'),
  getStaff: () => api.get('/admin/staff'),
  updateStaffRole: (userId, adminRole) => api.put(`/admin/staff/${userId}/role`, { admin_role: adminRole }),
  createStaffMember: (data) => api.post('/admin/staff', data),
  removeStaffMember: (userId) => api.delete(`/admin/staff/${userId}`),
  
  // Super Admin: User Role Management
  getAllAdmins: () => api.get('/admin/all-admins'),
  changeUserRole: (userId, role, adminRole = null, customPermissions = null) => 
    api.put(`/admin/users/${userId}/change-role`, { 
      role, 
      admin_role: adminRole,
      custom_permissions: customPermissions 
    }),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  
  // Super Admin: Backup
  backupAdmins: () => api.post('/admin/backup-admins'),
  
  // Bulk Price Management
  bulkPriceUpdate: (data) => api.post('/admin/products/bulk-price-update', data),
  getPriceStats: (sellerId) => api.get('/admin/products/price-stats', { params: sellerId ? { seller_id: sellerId } : {} }),
  
  // Shipping Management
  bulkShippingUpdate: (data) => api.post('/admin/products/bulk-shipping-update', data),
  getShippingStats: (sellerId) => api.get('/admin/shipping/stats', { params: sellerId ? { seller_id: sellerId } : {} }),
  getSellerShippingSettings: (sellerId) => api.get(`/admin/sellers/${sellerId}/shipping-settings`),
  updateSellerShippingSettings: (sellerId, data) => api.put(`/admin/sellers/${sellerId}/shipping-settings`, data),

  /** XML'de saklanan category_tr / category_path ile ürün category alanını geri yükle */
  restoreXmlCategories: (data) => api.post('/admin/products/restore-xml-categories', data),

  // Translation Management
  getTranslations: (category) => api.get('/admin/translations', { params: category ? { category } : {} }),
  getTranslationCategories: () => api.get('/admin/translations/categories'),
  createTranslation: (data) => api.post('/admin/translations', data),
  updateTranslation: (id, data) => api.put(`/admin/translations/${id}`, data),
  deleteTranslation: (id) => api.delete(`/admin/translations/${id}`),
  getSellerTranslationStats: () => api.get('/admin/seller-translation-stats'),
  translateMissingProducts: (payload) => api.post('/admin/products/translate-missing', payload),
  
  // Mail (Mailjet) settings — Sistem yönetimi
  getMailSettings: () => api.get('/admin/mail-settings'),
  updateMailSettings: (data) => api.put('/admin/mail-settings', data),
  getOutboundEmails: (params) => api.get('/admin/outbound-emails', { params }),
  getOutboundEmail: (id) => api.get(`/admin/outbound-emails/${id}`),
  testEmail: (data) => api.post('/admin/test-email', data),

  getWhatsappChatSettings: () => api.get('/whatsapp/admin/settings'),
  updateWhatsappChatSettings: (data) => api.put('/whatsapp/admin/settings', data),
  testWhatsappChat: (data) => api.post('/whatsapp/admin/test', data),

  getParasutIntegrationStatus: () => api.get('/admin/integrations/parasut/status'),
  startParasutConnect: () => api.post('/admin/integrations/parasut/connect'),
  disconnectParasut: () => api.post('/admin/integrations/parasut/disconnect'),

  getLastikcimIntegrationSettings: () => api.get('/admin/integrations/lastikcim/settings'),
  updateLastikcimIntegrationSettings: (data) => api.put('/admin/integrations/lastikcim/settings', data),
  updateLastikcimListingEnabled: (data) => api.put('/admin/integrations/lastikcim/listing-enabled', data),

  /** B2B entegrasyonlar (Lastikçim vb.) */
  lastikcimIntegrationStatus: () => api.get('/admin/integrations/lastikcim/status'),
  lastikcimIntegrationRun: () => api.post('/admin/integrations/lastikcim/run'),
  lastikcimRunDetail: (runId) => api.get(`/admin/integrations/lastikcim/runs/${runId}`),
  exportLastikcimProductsExcel: () =>
    api.get('/admin/integrations/lastikcim/products-export', { responseType: 'blob' }),

  /** markup_percent göndermezsen kayıtlı yüzde kullanılır */
  previewLastikcimPricing: (params = {}) =>
    api.get('/admin/integrations/lastikcim/pricing-preview', {
      params: typeof params.markup_percent === 'number' ? { markup_percent: params.markup_percent } : {},
    }),

  /** Lastik ve diğer randevular (admin liste) */
  tireAppointmentsList: (params) => api.get('/admin/tire-appointments', { params }),

  /** Kariyer / iş başvuru formları */
  careerApplicationsList: (params) => api.get('/admin/careers/applications', { params }),
  careerApplicationGet: (id) => api.get(`/admin/careers/applications/${id}`),

  newsletterSubscriptionsList: (params) => api.get('/admin/newsletter/subscriptions', { params }),
  newsletterUnsubscribe: (id) => api.put(`/admin/newsletter/subscriptions/${id}/unsubscribe`),
  newsletterDelete: (id) => api.delete(`/admin/newsletter/subscriptions/${id}`),

  membershipsList: (params) => api.get('/admin/memberships', { params }),
  membershipsGetSettings: () => api.get('/admin/memberships/settings'),
  membershipsUpdateSettings: (data) => api.put('/admin/memberships/settings', data),

};

export const newsletter = {
  subscribe: (email, locale = 'tr') =>
    api.post('/newsletter/subscribe', { email, locale, source: 'footer' }),
  unsubscribe: (token) =>
    api.get('/newsletter/unsubscribe', { params: { token } }),
};

/** Randevu güncelleme (admin / sahip) — `/tire-appointments` router */
export const tireAppointments = {
  update: (id, data) => api.put(`/tire-appointments/${id}`, data),
};

// Shipping
export const shipping = {
  rates: (country, weight) => api.get('/shipping/rates', { params: { country, weight } }),
  track: (trackingNumber) => api.get(`/shipping/track/${trackingNumber}`),
};

// Restaurants
export const restaurants = {
  list: (params) => api.get('/restaurants', { params }),
  get: (id) => api.get(`/restaurants/${id}`),
  createOrder: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/order`, data),
};

// Supermarket
export const supermarket = {
  // Catalog endpoints
  getCatalog: (params) => api.get('/supermarket/catalog', { params }),
  getCategories: () => api.get('/supermarket/catalog/categories'),
  getBrands: () => api.get('/supermarket/catalog/brands'),
  getCatalogProduct: (id) => api.get(`/supermarket/catalog/${id}`),
  
  // Seller endpoints
  getMyProducts: () => api.get('/seller/supermarket/products'),
  addProduct: (data) => api.post('/seller/supermarket/products', data),
  addManualProduct: (data) => api.post('/seller/supermarket/products/manual', data),
  updateProduct: (id, data) => api.put(`/seller/supermarket/products/${id}`, null, { params: data }),
  deleteProduct: (id) => api.delete(`/seller/supermarket/products/${id}`),
  
  // Store settings
  getStoreSettings: () => api.get('/seller/supermarket/store-settings'),
  updateStoreSettings: (data) => api.put('/seller/supermarket/store-settings', data),
  
  // Product Import endpoints
  getImportCategories: () => api.get('/seller/import/categories'),
  getImportTemplate: (format) => api.get(`/seller/import/template/${format}`, { responseType: 'blob' }),
  getSupportedSites: () => api.get('/seller/import/supported-sites'),
  importFromCSV: (formData) => api.post('/seller/import/csv', formData),
  importFromURL: (data) => api.post('/seller/import/url', data),
  importFromAPI: (data) => api.post('/seller/import/api', data),
  saveImportedProducts: (products) => api.post('/seller/import/save', products),
  categorizeProducts: (products) => api.post('/seller/import/categorize', { products }),
  getImportHistory: (limit = 20) => api.get('/seller/import/history', { params: { limit } }),
  bulkEditProducts: (productIds, updates) => api.post('/seller/import/bulk-edit', { product_ids: productIds, updates }),
  deleteImportBatch: (importId, deleteProducts = false) => api.delete(`/seller/import/history/${importId}`, { params: { delete_products: deleteProducts } }),
  
  // Marketplace Integration endpoints
  getMarketplaceIntegrations: () => api.get('/seller/marketplace/integrations'),
  getMarketplaceConnections: () => api.get('/seller/marketplace/connections'),
  connectMarketplace: (platform, credentials) => api.post('/seller/marketplace/connect', { platform, credentials }),
  disconnectMarketplace: (platform) => api.delete(`/seller/marketplace/disconnect/${platform}`),
  fetchMarketplaceProducts: (platform) => api.post(`/seller/marketplace/fetch-products/${platform}`),
  syncMarketplaceProducts: (platform, productIds) => api.post(`/seller/marketplace/sync/${platform}`, { product_ids: productIds }),
  
  // Public store endpoints
  getStores: (params) => api.get('/supermarket/stores', { params }),
  getStoreProducts: (sellerId, params) => api.get(`/supermarket/stores/${sellerId}/products`, { params }),
  getLocations: () => api.get('/supermarket/locations'),
  
  // Orders
  createOrder: (data) => api.post('/supermarket/orders', data),
  getOrders: () => api.get('/supermarket/orders'),
  getOrder: (id) => api.get(`/supermarket/orders/${id}`),
  
  // Seller orders
  getSellerOrders: (params) => api.get('/seller/supermarket/orders', { params }),
  updateOrderStatus: (orderId, status) => api.put(`/seller/supermarket/orders/${orderId}/status?status=${status}`),
};

// Turkish Markets (worldwide Turkish grocery stores)
export const turkishMarkets = {
  list: (params) => api.get('/markets', { params }),
  get: (id) => api.get(`/markets/${id}`),
  getCountries: () => api.get('/markets/countries'),
  getCities: (country) => api.get('/markets/cities', { params: { country } }),
  getStats: () => api.get('/markets/stats'),
  claim: (data) => api.post('/markets/claim', data),
  searchForClaim: (params) => api.get('/markets/claim/search', { params }),
};

// Finance
export const finance = {
  dashboard: () => api.get('/finance/dashboard'),
  transactions: (params) => api.get('/finance/transactions', { params }),
  invoices: (params) => api.get('/finance/invoices', { params }),
  getInvoice: (id) => api.get(`/finance/invoices/${id}`),
  generateInvoice: (data) => api.post('/finance/invoices/generate', data),
  commissions: () => api.get('/finance/commissions'),
  sellerReport: (sellerId) => api.get(`/finance/seller/${sellerId}/report`),
  refunds: (params) => api.get('/finance/refunds', { params }),
  processRefund: (orderId, reason) => api.post(`/finance/refund/${orderId}`, null, { params: { reason } }),
  // Seller finance
  sellerDashboard: () => api.get('/seller/finance/dashboard'),
  sellerOrders: (params) => api.get('/seller/finance/orders', { params }),
};

// Taxi & Limousine - Carrier
export const taxiCarrier = {
  // Vehicle Classes
  getVehicleClasses: () => api.get('/taxi/vehicle-classes'),
  // Carrier Profile
  getProfile: () => api.get('/taxi/carrier/profile'),
  saveProfile: (data) => api.post('/taxi/carrier/profile', data),
  // Vehicles
  getVehicles: () => api.get('/taxi/carrier/vehicles'),
  createVehicle: (data) => api.post('/taxi/carrier/vehicles', data),
  updateVehicle: (id, data) => api.put(`/taxi/carrier/vehicles/${id}`, data),
  deleteVehicle: (id) => api.delete(`/taxi/carrier/vehicles/${id}`),
  // Price Profiles
  getPriceProfiles: () => api.get('/taxi/carrier/price-profiles'),
  createPriceProfile: (data) => api.post('/taxi/carrier/price-profiles', data),
  updatePriceProfile: (id, data) => api.put(`/taxi/carrier/price-profiles/${id}`, data),
  deletePriceProfile: (id) => api.delete(`/taxi/carrier/price-profiles/${id}`),
  // Service Areas
  getServiceAreas: () => api.get('/taxi/carrier/service-areas'),
  createServiceArea: (data) => api.post('/taxi/carrier/service-areas', data),
  deleteServiceArea: (id) => api.delete(`/taxi/carrier/service-areas/${id}`),
  // Booking Requests
  getRequests: (params) => api.get('/taxi/carrier/requests', { params }),
  createOffer: (requestId, data) => api.post(`/taxi/carrier/requests/${requestId}/offer`, data),
  // Trips
  getTrips: (params) => api.get('/taxi/carrier/trips', { params }),
  updateTripStatus: (tripId, status) => api.put(`/taxi/carrier/trips/${tripId}/status`, null, { params: { status } }),
  // Stats & Rating
  getStats: () => api.get('/taxi/carrier/stats'),
  getRatingDetails: () => api.get('/taxi/carrier/rating-details'),
};

// Taxi & Limousine - Customer Booking
export const taxiBooking = {
  // Search
  searchVehicles: (params) => api.get('/taxi/search', { params }),
  getPopularRoutes: () => api.get('/taxi/popular-routes'),
  placeSuggestions: (q) => api.get('/taxi/place-suggestions', { params: { q } }),
  // Booking Requests
  createRequest: (data) => api.post('/taxi/booking-request', data),
  getRequest: (id) => api.get(`/taxi/booking-request/${id}`),
  acceptOffer: (requestId, offerId, paymentMethod) => 
    api.post(`/taxi/booking-request/${requestId}/accept-offer/${offerId}`, null, { params: { payment_method: paymentMethod } }),
  // My Bookings
  getMyBookings: (params) => api.get('/taxi/my-bookings', { params }),
  getMyRequests: (params) => api.get('/taxi/my-booking-requests', { params }),
  getBooking: (id) => api.get(`/taxi/booking/${id}`),
  cancelBooking: (id) => api.post(`/taxi/booking/${id}/cancel`),
  rateBooking: (id, data) => api.post(`/taxi/booking/${id}/rate`, data),
};

// Taxi & Limousine - Live Tracking
export const taxiTracking = {
  // Driver endpoints
  updateDriverLocation: (location) => api.post('/taxi/tracking/driver/location', location),
  updateDriverStatus: (status) => api.put('/taxi/tracking/driver/status', { status }),
  getDriverStatus: () => api.get('/taxi/tracking/driver/status'),
  // Dispatcher endpoints
  getDispatcherDrivers: () => api.get('/taxi/tracking/dispatcher/drivers'),
  // Customer endpoints
  getBookingTracking: (bookingId) => api.get(`/taxi/tracking/booking/${bookingId}`),
  getNearbyDrivers: (latitude, longitude, radiusKm = 10) => 
    api.get('/taxi/tracking/nearby-drivers', { params: { latitude, longitude, radius_km: radiusKm } }),
  // Trip control
  startTrip: (bookingId) => api.post(`/taxi/tracking/trip/${bookingId}/start`),
  completeTrip: (bookingId) => api.post(`/taxi/tracking/trip/${bookingId}/complete`),
  getTripHistory: (bookingId) => api.get(`/taxi/tracking/trip/${bookingId}/history`),
  // WebSocket URL helper
  getTrackingWsUrl: (bookingId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    return `${wsProtocol}//${wsHost}/api/taxi/tracking/ws/${bookingId}`;
  },
};

// Taxi & Limousine - Chat
export const taxiChat = {
  // Conversations
  getConversations: () => api.get('/taxi/chat/conversations'),
  createConversation: (bookingId, participantId) => 
    api.post('/taxi/chat/conversations', { booking_id: bookingId, participant_id: participantId }),
  getConversation: (conversationId) => api.get(`/taxi/chat/conversations/${conversationId}`),
  // Messages
  sendMessage: (conversationId, content, messageType = 'text') => 
    api.post(`/taxi/chat/conversations/${conversationId}/messages`, { content, message_type: messageType }),
  getMessages: (conversationId, before = null, limit = 50) => 
    api.get(`/taxi/chat/conversations/${conversationId}/messages`, { params: { before, limit } }),
  markAsRead: (conversationId) => api.post(`/taxi/chat/conversations/${conversationId}/read`),
  // Unread count
  getUnreadCount: () => api.get('/taxi/chat/unread-count'),
  // WebSocket URL helper
  getChatWsUrl: (conversationId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    return `${wsProtocol}//${wsHost}/api/taxi/chat/ws/${conversationId}`;
  },
};

// Taxi & Limousine - Payment (Revolut for international, Ziraat for Turkish)
export const taxiPayment = {
  // Create payment intent (supports revolut, ziraat, cash)
  createPaymentIntent: (bookingId, paymentMethod = 'revolut') => 
    api.post('/taxi/payment/create-intent', { booking_id: bookingId, payment_method: paymentMethod }),
  // Confirm payment
  confirmPayment: (bookingId, revolutOrderId) => 
    api.post('/taxi/payment/confirm', { booking_id: bookingId, revolut_order_id: revolutOrderId }),
  // Get payment status
  getPaymentStatus: (bookingId) => api.get(`/taxi/payment/status/${bookingId}`),
  // Process refund
  processRefund: (bookingId, amount, reason) => 
    api.post('/taxi/payment/refund', { booking_id: bookingId, amount, reason }),
  // Get payment methods
  getPaymentMethods: () => api.get('/taxi/payment/methods'),
  // Get payment history
  getPaymentHistory: (limit = 10) => api.get(`/taxi/payment/history?limit=${limit}`),
  // Process Ziraat payment (Turkish bank)
  processZiraatPayment: (bookingId, cardData) => 
    api.post('/taxi/payment/ziraat', { booking_id: bookingId, ...cardData }),
};

// Taxi & Limousine - SMS
export const taxiSms = {
  // Send booking confirmation
  sendBookingConfirmation: (bookingId) => 
    api.post('/taxi/sms/send-booking-confirmation', { booking_id: bookingId }),
  // Send driver arriving notification
  sendDriverArriving: (bookingId, etaMinutes) => 
    api.post('/taxi/sms/send-driver-arriving', { booking_id: bookingId, eta_minutes: etaMinutes }),
  // Send driver arrived notification
  sendDriverArrived: (bookingId) => 
    api.post('/taxi/sms/send-driver-arrived', { booking_id: bookingId }),
  // Send trip completed notification
  sendTripCompleted: (bookingId) => 
    api.post('/taxi/sms/send-trip-completed', { booking_id: bookingId }),
  // Send new offer notification
  sendNewOffer: (requestId, offerId) => 
    api.post('/taxi/sms/send-new-offer', { request_id: requestId, offer_id: offerId }),
  // Send booking for other notification
  sendBookingForOther: (bookingId, bookerName) => 
    api.post('/taxi/sms/send-booking-for-other', { booking_id: bookingId, booker_name: bookerName }),
  // Get SMS logs
  getLogs: (bookingId = null, limit = 50) => 
    api.get('/taxi/sms/logs', { params: { booking_id: bookingId, limit } }),
  // Get SMS service status
  getStatus: () => api.get('/taxi/sms/status'),
};

// Taxi & Limousine - Business (Corporate Accounts & Book for Others)
export const taxiBusiness = {
  // Corporate Account
  registerCorporate: (data) => api.post('/taxi/business/corporate/register', data),
  getCorporateAccount: () => api.get('/taxi/business/corporate/account'),
  updateCorporateAccount: (data) => api.put('/taxi/business/corporate/account', data),
  // Employees
  addEmployee: (data) => api.post('/taxi/business/corporate/employees', data),
  listEmployees: () => api.get('/taxi/business/corporate/employees'),
  removeEmployee: (employeeId) => api.delete(`/taxi/business/corporate/employees/${employeeId}`),
  // Travel Policies
  createTravelPolicy: (data) => api.post('/taxi/business/corporate/policies', data),
  listTravelPolicies: () => api.get('/taxi/business/corporate/policies'),
  // Corporate Booking
  corporateBook: (data) => api.post('/taxi/business/corporate/book', data),
  listCorporateBookings: (params) => api.get('/taxi/business/corporate/bookings', { params }),
  getCorporateReports: (period = 'month') => api.get('/taxi/business/corporate/reports', { params: { period } }),
  // Book for Others (Uber Central style)
  bookForOther: (data) => api.post('/taxi/business/book-for-other', data),
  listBookedForOthers: () => api.get('/taxi/business/booked-for-others'),
  // Dispatcher
  registerDispatcher: () => api.post('/taxi/business/dispatcher/register'),
  getDispatcherDashboard: () => api.get('/taxi/business/dispatcher/dashboard'),
  addDispatcherDriver: (data) => api.post('/taxi/business/dispatcher/drivers', data),
  listDispatcherDrivers: () => api.get('/taxi/business/dispatcher/drivers'),
  removeDispatcherDriver: (driverId) => api.delete(`/taxi/business/dispatcher/drivers/${driverId}`),
  assignTripToDriver: (bookingId, driverId) => 
    api.post('/taxi/business/dispatcher/assign-trip', { booking_id: bookingId, driver_id: driverId }),
};

// Taxi & Limousine - Driver Mobile App
export const taxiDriverApp = {
  // Profile
  getProfile: () => api.get('/taxi/driver-app/profile'),
  updateProfile: (data) => api.put('/taxi/driver-app/profile', data),
  // Location
  updateLocation: (location) => api.post('/taxi/driver-app/location', location),
  updateLocationBatch: (locations) => api.post('/taxi/driver-app/location/batch', locations),
  // Status
  getStatus: () => api.get('/taxi/driver-app/status'),
  updateStatus: (status) => api.put('/taxi/driver-app/status', { status }),
  goOnline: (location) => api.post('/taxi/driver-app/go-online', location),
  goOffline: () => api.post('/taxi/driver-app/go-offline'),
  // Trips
  getPendingTrips: () => api.get('/taxi/driver-app/trips/pending'),
  getActiveTrip: () => api.get('/taxi/driver-app/trips/active'),
  acceptTrip: (bookingId) => api.post(`/taxi/driver-app/trips/${bookingId}/accept`),
  rejectTrip: (bookingId, reason = null) => api.post(`/taxi/driver-app/trips/${bookingId}/reject`, { reason }),
  startTrip: (bookingId) => api.post(`/taxi/driver-app/trips/${bookingId}/start`),
  completeTrip: (bookingId) => api.post(`/taxi/driver-app/trips/${bookingId}/complete`),
  // Earnings
  getEarnings: (period = 'today') => api.get('/taxi/driver-app/earnings', { params: { period } }),
  getEarningsHistory: (page = 1, limit = 20) => api.get('/taxi/driver-app/earnings/history', { params: { page, limit } }),
  // Notifications
  getNotifications: (limit = 20) => api.get('/taxi/driver-app/notifications', { params: { limit } }),
  getUnreadCount: () => api.get('/taxi/driver-app/notifications/unread-count'),
};

// Seed
export const seed = () => api.post('/seed');

/** TRY bazlı canlı döviz kurları (1 TRY = rates_from_try[CCY]) */
export const exchangeRates = {
  get: (refresh = false) => api.get('/exchange-rates', { params: refresh ? { refresh: true } : {} }),
};

export default api;
